<template>
  <div :id="`${source}_meeting_transcript`">
    
    <div
      v-if="processedTranscriptData?.length > 0"
      ref="searchBarRef"
      class="px-2 pt-2"
    >
      <CustomInput
        format="text"
        v-model="searchQuery"
        placeholder="Search transcript"
        type="primary"
        :icon="imageNames.SEARCH_VARIANT1"
        size="size-5"
        alt="search_icon"
      />
    </div>

    <div
      v-if="filteredTranscriptData?.length > 0"
      class="flex flex-col px-1 py-[6px] overflow-y-scroll"
      :style="{ maxHeight: dynamicMaxHeight }"
    >
      <div
        v-for="(transcript, index) in filteredTranscriptData"
        :key="`${transcript?.participant_id}_transcript_${index}`"
        class="flex flex-row gap-2 py-[10px] px-2 rounded-lg cursor-pointer transcript_container"
        @click="seekToTranscriptTime(transcript.start_time)"
        :title="`Click to go to ${transcript.time}`"
      >
        <ProfileIcon :data="transcript" :font="12" :size="28" />

        <div class="flex flex-col gap-1">
          <div
            class="flex flex-row text-[11px] primary_color font-medium gap-1 items-center"
          >
            <span>{{ transcript?.name }}</span>
            <span>∙</span>
            <span class="secondary_color">{{ transcript?.time }}</span>
          </div>

          <span class="text-sm" v-html="highlightText(transcript?.text)"></span>
        </div>
      </div>
    </div>

    <EmptyScreen
      v-else-if="processedTranscriptData?.length > 0 && filteredTranscriptData?.length === 0"
      source="meeting_transcript"
      text="No matches found for your search"
    />

    <EmptyScreen
      v-else
      source="meeting_transcript"
      text="No transcript found yet!"
    />
  </div>
</template>

<script setup>
import { computed, inject, ref, onMounted } from "vue";
import { useStore } from "vuex";
import { formatSecondsToTime } from "@/src/utils/dateTimeUtils.js";
import { highlightSearchText } from "@/src/utils/htmlUtils.js";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  source: { type: String, required: true },
  data: { type: Object, required: true },
});

const store = useStore();
const videoPlayerHeight = inject("videoPlayerHeight", ref(0));
const searchQuery = ref("");
const searchBarRef = ref(null);
const searchBarHeight = ref(0);

const processedTranscriptData = computed(() => {
  try {
    if (!props.data?.transcript || !Array.isArray(props.data.transcript)) {
      return [];
    }

    return props.data.transcript.map((item, index) => ({
      id: item.participant_id || index,
      name: item.speaker,
      text: item.text,
      time: formatSecondsToTime(item.start_time),
      start_time: item.start_time,
      participant_id: item.participant_id,
    }));
  } catch (error) {
    console.error(
      "An error occurred while processing transcript data: ",
      error,
    );
  }
});

const filteredTranscriptData = computed(() => {
  try {
    if (!searchQuery.value || searchQuery.value.trim() === "") {
      return processedTranscriptData.value;
    }

    const query = searchQuery.value.toLowerCase().trim();
    return processedTranscriptData.value?.filter((item) => {
      const textMatch = item.text?.toLowerCase().includes(query);
      const speakerMatch = item.name?.toLowerCase().includes(query);
      return textMatch || speakerMatch;
    });
  } catch (error) {
    console.error("Error filtering transcript data:", error);
    return processedTranscriptData.value;
  }
});

const highlightText = (text) => highlightSearchText(text, searchQuery.value);

const dynamicMaxHeight = computed(() => {
  try {
    const getBannerHeight = () => {
      const bannerHeight = getComputedStyle(document.documentElement)
        .getPropertyValue("--banner-height")
        .replace("px", "");
      return parseInt(bannerHeight) || 0;
    };

    const baseOffset = 226;
    const bannerHeight = getBannerHeight();
    const videoHeight = videoPlayerHeight.value || 0;

    return `calc(100vh - ${baseOffset - bannerHeight + videoHeight + searchBarHeight.value}px)`;
  } catch (error) {
    console.error(
      "An error occurred while calculating dynamic max height: ",
      error,
    );
  }
});

const seekToTranscriptTime = (time) => {
  if (time !== undefined && time !== null) {
    store.dispatch("meetings/seekRecordingToTime", time);
  }
};

onMounted(() => {
  if (searchBarRef.value) {
    searchBarHeight.value = searchBarRef.value.offsetHeight;
  }
});
</script>

<style scoped>
.transcript_container {
  transition: background-color 0.2s ease-in-out;
}

.transcript_container:hover {
  background-color: var(--white-opacity-4);
}

:deep(.search-highlight) {
  background-color: var(--white-opacity-20);
  color: var(--text-color);
  border-radius: 2px;
}
</style>
